import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import AgencyIdentityPanel from "@/components/AgencyIdentityPanel";

export default function Settings() {
    const { user, token } = useAuth();
    const [agency, setAgency] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/agency");
                if (alive) setAgency(data);
            } catch (e) {
                toast.error(`Could not load agency: ${e.userMessage || e.message}`);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const updateField = (key, value) => setAgency((a) => ({ ...(a || {}), [key]: value }));

    const save = async () => {
        if (!agency) return;
        setSaving(true);
        try {
            const { data } = await api.put("/agency", {
                agency_name: agency.agency_name || "",
                tagline: agency.tagline || "",
                primary_color: agency.primary_color || "#FF4400",
                contact_email: agency.contact_email || "",
                contact_phone: agency.contact_phone || "",
                website: agency.website || "",
                white_label: !!agency.white_label,
                template: agency.template || "hybrid",
            });
            setAgency(data);
            toast.success("Agency branding saved");
        } catch (e) {
            toast.error(`Save failed: ${e.userMessage || e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const t = localStorage.getItem("aivos_token") || token;
            const res = await fetch(`${BACKEND_URL}/api/agency/logo`, {
                method: "POST",
                headers: { Authorization: `Bearer ${t}` },
                body: fd,
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `${res.status}`);
            }
            const j = await res.json();
            setAgency((a) => ({ ...(a || {}), logo_path: j.logo_path }));
            toast.success("Logo uploaded — appears on every PDF/PPTX export");
        } catch (e) {
            toast.error(`Logo upload failed: ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 sm:p-8 lg:p-10 max-w-3xl mx-auto" data-testid="settings-page">
            <div className="mb-8">
                <div className="label-micro mb-2">Workspace</div>
                <h1 className="font-display text-4xl font-medium tracking-tight">Settings</h1>
            </div>

            {/* ---- Agency Identity (Phase J) — onboarding + intelligence + approval ---- */}
            {!loading && agency && (
                <AgencyIdentityPanel agency={agency} setAgency={setAgency} />
            )}

            {/* ---- Agency branding ---- */}
            <div className="card-base p-6 mb-6" data-testid="agency-branding-card">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="label-micro mb-1">Agency branding</div>
                        <div className="text-sm text-zinc-400">
                            Header logo + “Prepared by …” footer wraps every PDF and PPTX export.
                        </div>
                    </div>
                    {agency?.logo_path ? (
                        <span className="text-xs text-emerald-400 font-mono">logo uploaded</span>
                    ) : (
                        <span className="text-xs text-zinc-500 font-mono">no logo</span>
                    )}
                </div>

                {loading ? (
                    <div className="text-sm text-zinc-500">Loading…</div>
                ) : (
                    <div className="space-y-4">
                        <Field label="Agency name">
                            <input
                                data-testid="agency-name-input"
                                className={INPUT_CLS}
                                value={agency?.agency_name || ""}
                                onChange={(e) => updateField("agency_name", e.target.value)}
                                placeholder="e.g. RIFA Productions"
                            />
                        </Field>
                        <Field label="Tagline (optional)">
                            <input
                                data-testid="agency-tagline-input"
                                className={INPUT_CLS}
                                value={agency?.tagline || ""}
                                onChange={(e) => updateField("tagline", e.target.value)}
                                placeholder="e.g. Strategic Creative Studio"
                            />
                        </Field>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Contact email">
                                <input
                                    data-testid="agency-email-input"
                                    className={INPUT_CLS}
                                    value={agency?.contact_email || ""}
                                    onChange={(e) => updateField("contact_email", e.target.value)}
                                />
                            </Field>
                            <Field label="Website">
                                <input
                                    data-testid="agency-website-input"
                                    className={INPUT_CLS}
                                    value={agency?.website || ""}
                                    onChange={(e) => updateField("website", e.target.value)}
                                />
                            </Field>
                        </div>
                        <Field label="Header accent color (hex)">
                            <div className="flex items-center gap-3">
                                <input
                                    data-testid="agency-color-input"
                                    type="color"
                                    className="h-10 w-16 bg-zinc-950 border border-zinc-800 rounded"
                                    value={agency?.primary_color || "#FF4400"}
                                    onChange={(e) => updateField("primary_color", e.target.value)}
                                />
                                <span className="font-mono text-xs text-zinc-400">
                                    {agency?.primary_color || "#FF4400"}
                                </span>
                            </div>
                        </Field>
                        <Field label="Logo">
                            <div className="flex items-center gap-3">
                                <button
                                    data-testid="agency-logo-upload-btn"
                                    onClick={() => fileRef.current?.click()}
                                    className="px-4 py-2 border border-zinc-800 rounded bg-zinc-950 hover:bg-zinc-900 text-sm"
                                    disabled={saving}
                                >
                                    {saving ? "Uploading…" : "Upload logo"}
                                </button>
                                <input
                                    type="file"
                                    ref={fileRef}
                                    accept="image/png,image/jpeg,image/webp"
                                    className="hidden"
                                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                                />
                                <span className="text-xs text-zinc-500">
                                    PNG / JPG / WebP. Recommended 400×100, transparent BG.
                                </span>
                            </div>
                        </Field>
                        <div className="flex items-center justify-between pt-3 border-t border-zinc-900">
                            <div className="mt-1">
                                <div className="text-xs uppercase tracking-wider font-mono text-zinc-500 mb-2">Output Template</div>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { id: "corporate", name: "Corporate Strategy", desc: "Chart-heavy · structured" },
                                        { id: "hybrid", name: "Hybrid", desc: "Balanced (default)" },
                                        { id: "creative", name: "Creative Pitch", desc: "Bold · visual-first" },
                                    ].map(t => (
                                        <button
                                            key={t.id}
                                            data-testid={`agency-template-${t.id}`}
                                            onClick={() => updateField("template", t.id)}
                                            className={`text-left border rounded-sm p-3 transition ${(agency?.template || "hybrid") === t.id ? "border-orange-500 bg-orange-500/10" : "border-zinc-800 hover:border-zinc-700"}`}
                                        >
                                            <div className="text-sm font-medium text-zinc-100">{t.name}</div>
                                            <div className="text-[11px] text-zinc-500 mt-0.5">{t.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <label
                                className="flex items-center gap-3 cursor-pointer select-none"
                                data-testid="agency-white-label-toggle"
                            >
                                <input
                                    type="checkbox"
                                    checked={!!agency?.white_label}
                                    onChange={(e) => updateField("white_label", e.target.checked)}
                                    className="accent-orange-500 h-4 w-4"
                                />
                                <span className="text-sm text-zinc-200">
                                    White-label mode
                                </span>
                                <span className="text-xs text-zinc-500">
                                    (hides “Powered by Furmais” from exports)
                                </span>
                            </label>
                            <button
                                data-testid="agency-save-btn"
                                onClick={save}
                                disabled={saving}
                                className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded disabled:opacity-50"
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ---- Account ---- */}
            <div className="card-base p-6 space-y-4">
                <div className="label-micro mb-2">Account</div>
                <Row label="Name" value={user?.name} />
                <Row label="Email" value={user?.email} />
                <Row label="Role" value={user?.role} mono />
                <Row label="Account ID" value={user?.id} mono />
            </div>

            <div className="card-base p-6 mt-6">
                <div className="label-micro mb-2">LLM model preference</div>
                <div className="text-sm text-zinc-400">
                    Strategy stages run on <span className="font-mono text-white">Claude Sonnet 4.5</span>;
                    structured-list stages on <span className="font-mono text-white">Claude Haiku 4.5</span> for speed.
                    Architecture is model-agnostic — swap providers per request.
                </div>
            </div>

            <div className="card-base p-6 mt-6">
                <div className="label-micro mb-2">Roles</div>
                <ul className="text-sm text-zinc-300 space-y-1">
                    <li><span className="font-mono text-zinc-500 mr-2">owner</span>full access</li>
                    <li><span className="font-mono text-zinc-500 mr-2">manager</span>create + run agents</li>
                    <li><span className="font-mono text-zinc-500 mr-2">editor</span>edit outputs</li>
                    <li><span className="font-mono text-zinc-500 mr-2">client_viewer</span>review-only via shared link</li>
                </ul>
            </div>
        </div>
    );
}

const INPUT_CLS = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-orange-500/60";


const Field = ({ label, children }) => (
    <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
        {children}
    </div>
);

const Row = ({ label, value, mono }) => (
    <div className="flex items-center justify-between border-b border-zinc-900 pb-3 last:border-b-0">
        <span className="text-zinc-500 text-sm">{label}</span>
        <span className={`text-zinc-100 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
);
